import { sortBy } from "lodash";
import { DataCardinalities, DataTypes } from "../../../data/DataManager";
import { EMPTY_VALUE, FieldSchema, FieldValue, FieldValues } from "../../../Schema";
import { ErrorMessage } from "../../../validation/ValidationManager";
import { SubEquipmentConnection, SubEquipmentItem, keysToSnakeCase } from "../../cluster";

/**
 * A cluster is an assembly, so a single component is not one — the picker and the
 * preview both have to refuse it.
 */
export const MIN_CLUSTER_COMPONENTS = 2;

/**
 * Cross-field rule shared by `subEquipments` and `subEquipmentsPreview`: both steps of
 * the cluster wizard report the same shortfall, and both read the count off the same
 * `subEquipments` value. It lives on the fields rather than in a caller so the form,
 * the review flow and any future headless consumer apply it alike.
 */
export const validateClusterComponentCount = ({ subEquipments }: FieldValues): ErrorMessage => {
  return Array.isArray(subEquipments) && subEquipments.length >= MIN_CLUSTER_COMPONENTS ?
    EMPTY_VALUE :
    `A cluster must contain at least ${MIN_CLUSTER_COMPONENTS} components.`;
};

/**
 * Sub-equipments that make up the cluster. Rendered through a custom picker
 * input supplied by the frontend structure (ClusterComponentsPicker); the schema
 * loads the rows from the GraphQL `subEquipment` connection, ordered by position
 * and with snake-cased keys so they line up with the grid columns.
 */
export const subEquipments: FieldSchema = {
  name: "subEquipments",
  // The form field is `subEquipments`, but the GraphQL source is the
  // `subEquipment` connection. Declare it explicitly so the schema-derived
  // Selection fetches the nested items (the `fromGraphql` getter below reads
  // `subEquipment.items`). Mirrors the app's `getDigitalLabInstrumentRepositoryEntry`
  // query.
  graphql: {
    selection: `subEquipment {
      items {
        id
        belongingToGroup
        buildingLocation { key value }
        checkInGracePeriod
        checkInRequired
        checkInStartPeriod
        cluster
        clusterId
        comment
        configurationBaseline
        costCenter
        csv
        dateOfLastMaintanance
        dateOfNextMaintanance
        dateOfNextPeriodicReview
        documentedMaintenanceDate
        displayImage
        electronicRecord
        electronicSignatures
        entryType
        equipmentAdministrator
        equipmentCategory
        equipmentId
        equipmentModel
        equipmentNickName
        floor { key value }
        functionalLocation
        gxpRelevant
        installedTests { name version }
        instrumentGTIN
        instrumentRUDI
        inventoryNumber
        isBookable
        isHazardous
        isClusterSWVersion
        isVisualized
        linkedInstance
        location
        maintenanceIntervalInDays
        maintenancePlan
        manufacturer
        materialNumber
        modelName
        network
        positionInCluster
        qualificationDocuments { isSynchronized value { documentId name } }
        qualificationStatus
        controlledEquipmentStatus
        responsiblePerson
        responsibleProxy
        room { key value }
        secondaryID
        secondaryIDMapping
        serialNumber
        shouldAssisted
        siteId
        siteName
        siteTimezone
        softwareVersion
        sop { key value }
        status
        systemOwner
        systemStatus
        variant
        linkedInstanceRef { isActive linkedInstance secondaryIdMapping }
      }
    }`,
  },
  data: {
    type: DataTypes.OBJECT,
    active: true,
    cardinality: DataCardinalities.MULTIPLE,
    minCount: MIN_CLUSTER_COMPONENTS,
  },
  validation: {
    required: true,
    custom: {
      getter: validateClusterComponentCount,
      fields: ["subEquipments"],
    },
  },
  dataTransform: {
    fromGraphql: {
      getter: ({ subEquipment }: FieldValues) => {
        const items = (subEquipment as SubEquipmentConnection)?.items ?? [];
        return sortBy(items, "positionInCluster").map(keysToSnakeCase) as FieldValue;
      },
      fields: ["subEquipment"],
    },
    toGraphql: {
      getter: ({ subEquipments, leadingSoftwareVersionId }: FieldValues) => {
        if (!Array.isArray(subEquipments)) {
          return [];
        }
        return (subEquipments as SubEquipmentItem[]).map((item) => ({
          id: item?.id ?? null,
          positionInCluster: item?.position_in_cluster ?? item?.positionInCluster ?? null,
          isClusterSWVersion: Boolean(leadingSoftwareVersionId) && item?.id === leadingSoftwareVersionId,
        }));
      },
      fields: ["subEquipments", "leadingSoftwareVersionId"],
    },
  },
};
